import styles from "./notification.module.css";
import Helmet from "react-helmet";
import Footer from "../../components/Footer";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import { Home, X } from "react-feather";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import NotificationCard from "./components/NotificationCard";
import NotificationFilter from "./components/NotificationFilter";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { TableLoaderSmall } from "../../components/LoaderC";
import { toast } from 'react-smart-toaster';
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";
import useDebouncedValue from "../../hooks/useDebouncedValue";

const CHUNK_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const Notification = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [allData, setAllData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", type: "" });
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(false);

  const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);
  const sentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const fetchNotifications = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { offset: currentOffset, limit: CHUNK_SIZE };
    if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
    if (filterData.type !== "" && filterData.type !== "all") params.type = filterData.type;

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios.get(process.env.REACT_APP_USER_API_URL + "v1/email-sms-contents/paginated", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      // Ignore responses from requests superseded by newer filters
      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};
      const nextOffset = meta?.next_offset ?? (currentOffset + newChunk.length);

      setAllData((prev) => (replace ? newChunk : [...prev, ...newChunk]));
      setOffset(nextOffset);
      setHasMore(meta?.has_more ?? (newChunk.length > 0 && nextOffset < (meta?.total ?? 0)));
      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error('[Notification] Failed to load notification list', error);
      toast.error(error?.response?.data?.message || 'Unable to load notifications. Please try again.');

      if (replace) {
        setLoadError(true);
        setAllData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, debouncedSearch, filterData.type]);

  // Load the first chunk whenever search or filters change
  useEffect(() => {
    fetchNotifications(0, true);
  }, [fetchNotifications]);

  // Infinite scroll: load the next chunk when the sentinel enters the viewport
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchNotifications(offset, false);
      }
    }, { threshold: 0.1 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchNotifications]);

  const changeStatusConfirm = async (data) => {
    setLoading(true);
    setArchiveModalOpen(false);
    setPublishModalOpen(false);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    axios.put(process.env.REACT_APP_USER_API_URL + 'v1/email-sms-contents/' + selectedItem?.id, { is_archived: data }, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setLoading(false);

      setSnackbarMsg("Email campaign updated successfully.");
      setSnackbarOpen(true);

      setAllData(prev => {
        return prev?.map((item) => {
          if (item.id === selectedItem?.id) {
            return { ...item, is_archived: data };
          }
          return item;
        })
      });

      setSelectedItem(null);
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  return (<>
    <Helmet title="Manage notification" />

    {loading && <Loader />}

    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Manage notification</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Notification</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Manage notification</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>

          {contentLoading && (<div className="Loader TableLoader">
            <TableLoaderSmall />
          </div>)}

          {!contentLoading && (<div className="control-pane">
            <div className={`${styles.Row}`}>

              <NotificationFilter filterData={filterData} setFilterData={setFilterData.bind(this)} />

              {allData.length > 0 && <>
                <div className={refetching ? `${styles.LogMaiSec} ${styles.Refetching}` : `${styles.LogMaiSec}`}>

                  {allData?.map((item, index) => {
                    return <NotificationCard key={item?.id ?? index} item={item} setArchiveModalOpen={setArchiveModalOpen.bind(this)} setPublishModalOpen={setPublishModalOpen.bind(this)} setSelectedItem={setSelectedItem.bind(this)} />;
                  })}

                  {/* Sentinel: entering the viewport loads the next chunk */}
                  <div ref={sentinelRef} className={styles.ScrollSentinel} />
                </div>

                {loadingMore && (
                  <div className={styles.gridScrollStatus}>
                    <div className={styles.scrollLoaderWrap}>
                      <div className={styles.scrollSpinner} />
                      <span>Loading more notifications...</span>
                    </div>
                  </div>
                )}

                {loadMoreError && (
                  <div className={styles.gridScrollStatus}>
                    <p>Couldn't load more notifications.</p>
                    <button type="button" className={styles.RetryBu} onClick={() => fetchNotifications(offset, false)}>Retry</button>
                  </div>
                )}
              </>}

              {allData.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                <p className={`${styles.NoDataText}`}>Something went wrong</p>
                <p className={`${styles.NoDataTextSub}`}>We couldn't load notifications. Please try again.</p>
                <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchNotifications(0, true)}>Retry</button>
              </div>)}

              {allData.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                <div className={`${styles.NoDataIMG}`}>
                  <img src="/static/img/no-prospect.png" alt="No Data" />
                </div>
                <p className={`${styles.NoDataText}`}>Let's get started!</p>
                <p className={`${styles.NoDataTextSub}`}>You don't have any notification saved.</p>
              </div>)}

            </div>
          </div>)}
        </div>
      </div>
    </div>

    <Dialog
      open={archiveModalOpen}
      onClose={(e) => setArchiveModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">Are you sure you want to archive email campaign?<button onClick={(e) => setArchiveModalOpen(false)}><X /></button></DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <div className="ModalFormGroup">
            <label className="PopupBodyText">The lemail campaign will be archived immediately. You can publish it at any time.</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <button onClick={(e) => setArchiveModalOpen(false)} className="CancelPopupBU">Cancel</button>
        <button className="SubmitPopupBU" onClick={(e) => changeStatusConfirm(1)}>Archive</button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={publishModalOpen}
      onClose={(e) => setPublishModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">Are you sure you want to publish email campaign?<button onClick={(e) => setPublishModalOpen(false)}><X /></button></DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <div className="ModalFormGroup">
            <label className="PopupBodyText">The email campaign will be published immediately. You can unpublish it at any time.</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <button onClick={(e) => setPublishModalOpen(false)} className="CancelPopupBU">Cancel</button>
        <button className="SubmitPopupBU" onClick={(e) => changeStatusConfirm(0)}>Publish</button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </>
  )
}

export default withCustomAuthenticationRequired(Notification, {
  onRedirecting: () => <Loader />,
});